import React, { useEffect, useRef, useState } from 'react';
import RoomSheet from './RoomSheet';

// 📣 Promote (SPACES_REDESIGN_BRAINSTORM_2026_09_29 §3.8; Nina 09-29: "Promote space button
// for creators (to easily share links to space in order to get $)"). A share sheet for the
// room's MOD; since 10-01 (Nina: "shared/promoted by everyone as long as they're not
// private/unlisted") also for every visitor in a PUBLIC room. The page passes `canPromote`
// (utils/roomChrome.canPromote) and nothing renders without it; `isMod` (this device made
// the room) only picks the words: "my room" is the maker's, a visitor says "this room".
//   · the link is /game-step?gameId=<id>&charge=1 (charge=1 opens the battery note on
//     arrival). It is built from the room id ONLY: never the page URL, so a playerId or a
//     seat secret sitting in the address bar can never ride into a share.
//   · a prewritten caption you can edit; plain-text chips open Reddit / Bluesky / X share
//     pages in a new tab; Copy (clipboard, with a select-the-text fallback); "More…" hands
//     it to the phone's own share sheet when there is one (shareRoomLink's navigator.share).
//   · no QR (no new deps).
// Look + armor: RoomSheet (C's neutral sheet, `rsh-` classes, data-prop-ui buttons).

const oneLine = (s) => String(s == null ? '' : s).replace(/\s+/g, ' ').trim();
const clamp = (s, n) => (s.length > n ? `${s.slice(0, n - 1).trimEnd()}…` : s);
export const CAPTION_MAX = 280;
export const PROMOTE_BATTERY_LINE = 'Links open with the battery up, so friends can chip in.';
// 09-30 (review): "chip in" only where there is somewhere to chip in. Until the
// room's battery carries a support link (HOUSE_SUPPORT_URL is unset live), the
// sheet says only what ?charge=1 really does.
export const PROMOTE_BATTERY_LINE_PLAIN = 'Links open with the battery up.';

/** The footer line: the chip-in promise only when `canChipIn` (the battery has a support link). */
export const promoteBatteryLine = (canChipIn) => (canChipIn === true ? PROMOTE_BATTERY_LINE : PROMOTE_BATTERY_LINE_PLAIN);

/** The share link: the room id and charge=1, nothing else. */
export function promoteLink(gameId, origin) {
  const base = String(origin || '').replace(/\/+$/, '');
  return `${base}/game-step?gameId=${encodeURIComponent(String(gameId || ''))}&charge=1`;
}

/** "Come hang in {Room}. {tagline or a short line}" (no name: "my room" for its mod, else "this room") */
export function defaultCaption(roomName, tagline, { mine = true } = {}) {
  const room = clamp(oneLine(roomName), 80) || (mine ? 'my room' : 'this room');
  let line = clamp(oneLine(tagline), 140);
  if (line && !/[.!?…]$/.test(line)) line = `${line}.`;
  // a room named "Stream and Tea!" (or clamped to "…") already ends its sentence: no "!." (review 09-29)
  const stop = /[.!?…]$/.test(room) ? '' : '.';
  return `Come hang in ${room}${stop} ${line || 'Pull up a chair.'}`;
}

/** Plain-text intent links (each opens that site's own share page; nothing posts by itself). */
export function shareIntents(link, caption) {
  const text = clamp(oneLine(caption), CAPTION_MAX);
  const enc = encodeURIComponent;
  // Bluesky counts the link inside its 300: keep the caption short enough for both
  const bsky = clamp(text, Math.max(40, 299 - link.length));
  return [
    { k: 'reddit', n: 'Reddit', url: `https://www.reddit.com/submit?url=${enc(link)}&title=${enc(text)}` },
    { k: 'bluesky', n: 'Bluesky', url: `https://bsky.app/intent/compose?text=${enc(`${bsky} ${link}`)}` },
    { k: 'x', n: 'X', url: `https://x.com/intent/tweet?text=${enc(text)}&url=${enc(link)}` },
  ];
}

async function copyText(text, fieldEl) {
  try {
    if (typeof navigator !== 'undefined' && navigator.clipboard && navigator.clipboard.writeText) {
      await navigator.clipboard.writeText(text);
      return 'copied';
    }
  } catch (e) { /* denied: fall back to selecting it */ }
  try {
    if (fieldEl) {
      fieldEl.focus();
      fieldEl.select();
      if (typeof document.execCommand === 'function' && document.execCommand('copy')) return 'copied';
      return 'selected';
    }
  } catch (e) { /* no selection API */ }
  return 'failed';
}

const COPY_WORDS = {
  copied: 'Copied.',
  selected: 'It’s selected. Copy it from there.',
  failed: 'Couldn’t copy. Select the link and copy it.',
};

export default function PromoteSheet({
  open, onClose, gameId, roomName, tagline = '', isMod = false, isMobile = false, accent = null, origin = null,
  // 10-01: who may open it at all (roomChrome.canPromote: everyone in a public room, the mod
  // in a hidden one). Left out = the mod only (isMod), the 09-29 rule.
  canPromote,
  // true only when the room's battery block carries a support link (the page's
  // meterView(...).support, which exists only with AiPower's SUPPORT_NOTE)
  canChipIn = false,
}) {
  const link = promoteLink(gameId, origin || (typeof window !== 'undefined' ? window.location.origin : ''));
  const allowed = canPromote === undefined ? !!isMod : !!canPromote;
  // read at open time only: a seat that resolves while the sheet is open never wipes an edit
  const mineRef = useRef(!!isMod);
  mineRef.current = !!isMod;
  const [caption, setCaption] = useState(() => defaultCaption(roomName, tagline, { mine: mineRef.current }));
  const [said, setSaid] = useState('');
  const linkRef = useRef(null);
  const allRef = useRef(null);
  const saidTimer = useRef(null);

  // a fresh open gets a fresh caption for this room
  useEffect(() => {
    if (open) { setCaption(defaultCaption(roomName, tagline, { mine: mineRef.current })); setSaid(''); }
    return () => { if (saidTimer.current) clearTimeout(saidTimer.current); };
  }, [open, roomName, tagline]);

  if (!allowed || !gameId) return null;

  const say = (words) => {
    setSaid(words);
    if (saidTimer.current) clearTimeout(saidTimer.current);
    saidTimer.current = setTimeout(() => setSaid(''), 3000);
  };
  const copyLink = async () => say(COPY_WORDS[await copyText(link, linkRef.current)]);
  const copyAll = async () => say(COPY_WORDS[await copyText(`${oneLine(caption)} ${link}`, allRef.current)]);
  const openShare = (url) => { try { window.open(url, '_blank', 'noopener,noreferrer'); } catch (e) { /* popup blocked */ } };
  const canNativeShare = typeof navigator !== 'undefined' && typeof navigator.share === 'function';
  const nativeShare = () => {
    try { navigator.share({ title: oneLine(roomName) || 'myspaice', text: oneLine(caption), url: link }).catch(() => {}); } catch (e) { /* dismissed */ }
  };

  return (
    <RoomSheet
      open={open}
      onClose={onClose}
      title="Promote this room"
      sub={roomName || null}
      icon="📣"
      isMobile={isMobile}
      accent={accent}
      testId="promote-room"
      footer={(
        <>
          <span className="rsh-line" data-promote-battery="1"><span aria-hidden="true">🔋 </span>{promoteBatteryLine(canChipIn)}</span>
          <button type="button" className="rsh-act" data-prop-ui="" onClick={onClose}>Done</button>
        </>
      )}
    >
      <div className="rsh-lb">Link</div>
      <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
        <input
          ref={linkRef}
          className="rsh-field rsh-mono"
          type="text"
          readOnly
          value={link}
          aria-label="Room link"
          data-promote-link="1"
          onFocus={(e) => e.target.select()}
          style={{ flex: 1 }}
        />
        <button type="button" className="rsh-act rsh-pri" data-prop-ui="" data-promote-copy="1" onClick={copyLink}>Copy</button>
      </div>

      <div className="rsh-lb">Caption</div>
      <textarea
        className="rsh-field"
        rows={isMobile ? 3 : 2}
        value={caption}
        maxLength={CAPTION_MAX}
        aria-label="Caption"
        data-promote-caption="1"
        onChange={(e) => setCaption(e.target.value.slice(0, CAPTION_MAX))}
        style={{ resize: 'vertical', minHeight: 64 }}
      />
      {/* the text "Copy all" puts on the clipboard, for the select-it fallback */}
      <textarea ref={allRef} readOnly tabIndex={-1} aria-hidden="true" value={`${oneLine(caption)} ${link}`}
        style={{ position: 'fixed', top: 0, left: 0, width: 1, height: 1, opacity: 0, pointerEvents: 'none', border: 0, padding: 0 }} />

      <div className="rsh-lb">Share to</div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }} data-promote-shares="1">
        {shareIntents(link, caption).map((s) => (
          <button key={s.k} type="button" className="rsh-pill" data-prop-ui="" data-share={s.k} onClick={() => openShare(s.url)}>{s.n}</button>
        ))}
        <button type="button" className="rsh-pill" data-prop-ui="" data-share="copy" onClick={copyAll}>Copy all</button>
        {canNativeShare ? (
          <button type="button" className="rsh-pill" data-prop-ui="" data-share="more" onClick={nativeShare}>More…</button>
        ) : null}
      </div>
      <div className="rsh-fine" role="status" aria-live="polite" data-promote-said="1" style={{ color: '#ECEEF2', fontWeight: 600, minHeight: 18 }}>{said}</div>
    </RoomSheet>
  );
}
